import { createSelector } from 'reselect'
import {
  ContentReviewStatus,
  AnswerStatusFilterOptions,
  SessionPermission,
  ReviewStatus,
  UserRole,
  ContentSharingType,
  ContentFormRecordType,
} from '@tabeeb/enums'
import { tabeebTheme } from '@collaborate-health/uikit'
import { hasSessionPermission } from '@tabeeb/modules/permissions/selectors'
import { Approved, Rejected, Submitted } from '@tabeeb/modules/shared/icons'
import {
  getContentReviewStatus,
  getCurrentUserReview,
  getMergeReviewWithCurrentForm,
  getReviewsList,
} from '@tabeeb/modules/contentReviews/selectors'
import { getCurrentUserId } from '@tabeeb/modules/account/selectors'
import {
  getFormTitle,
  getIsFormRecordsEnabled,
  getIsFormSignatureEnabledInTenant,
  getIsInContentReviewEnabled,
  getIsShowFormUsersFilter,
  getShowExportFormToPdfButton,
  getShowMenuAI,
} from '@tabeeb/modules/appConfigState/selectors'
import { getIsContentReadonly } from '@tabeeb/modules/readonlyWhiteboard/selectors'
import { RootState } from '@tabeeb/store'

import {
  getIsCurrentUserReviewer,
  getIsCurrentUserViewer,
  getUsersListForCurrentUser,
} from '@tabeeb/modules/../users/selectors'
import { PluginUser } from '@tabeeb/modules/../users/types'

import { getContentId, getIsCurrentUserPresenter } from '@tabeeb/modules/shared/content/selectors'
import { AIObjectsInfoResponse, FormAnswerStatus } from '@tabeeb/contracts/main-api/swagger-main-api'
import { ContentReview } from '@tabeeb/modules/contentReviews/types'
import { FormButtons, FormButtonsGroup } from '../services/enums'
import { calculateValue } from '../services/calculateDefaultValueOrCounterLimit'
import { formTabsIdsEquals } from '../services/formTabService'
import { DisplayAnswersMode } from '../enums'
import { ContentForm, FormControl, FormControlAnswer } from '../types'

export const getSelectedGalleryItemId = (state: RootState) => state.gallery.galleryState.selectedGalleryItemId
export const getContentForms = (state: RootState) => state.forms.contentForms
export const contentFormsLoadingState = (state: RootState) => state.forms.contentFormsLoadingState
export const getCurrentForm = (state: RootState) => state.forms.currentForm
export const getCurrentFormId = (state: RootState) => getCurrentForm(state)?.Id
export const getCurrentFormName = (state: RootState) => getCurrentForm(state)?.Name

export const selectUsersFilterDeletedUserIds = (state: RootState) => state.forms.usersFilter.deletedUserIds
export const selectUsersFilterHideOtherUsers = createSelector(
  [getCurrentForm, getContentForms],
  (currentContentForm, preloadedContentForms) => {
    const preloadedCurrentContentForm = preloadedContentForms.find(
      (contentForm) => contentForm.Id === currentContentForm.Id
    )
    return preloadedCurrentContentForm?.HideOtherUsers ?? false
  }
)
export const selectUsersFilter = createSelector(
  [
    getCurrentUserId,
    getUsersListForCurrentUser,
    selectUsersFilterDeletedUserIds,
    selectUsersFilterHideOtherUsers,
    getIsShowFormUsersFilter,
  ],
  (currentUserId, users: PluginUser[], deletedUserIds, hideOtherUsers, showUsers) => {
    if (!showUsers) {
      return []
    }

    if (hideOtherUsers) {
      return users.filter((user) => user.id === currentUserId)
    }

    return users.filter((user) => !user.isDeleted || deletedUserIds.includes(user.id) || user.role === UserRole.AIUser)
  }
)
export const getSelectedUserId = (state: RootState) => state.forms.selectedUserId
export const getDisplayAnswersMode = createSelector(
  [(state: RootState) => state.forms.displayAnswersMode, selectUsersFilter, getSelectedUserId],
  (mode, users, selectedUserId) => {
    if (mode === DisplayAnswersMode.ByUser) {
      return users.some((user) => user.id === selectedUserId) ? DisplayAnswersMode.ByUser : DisplayAnswersMode.AllLatest
    }
    return mode
  }
)

export const getFormUsersFilterSelectedValue = createSelector(
  [getDisplayAnswersMode, getSelectedUserId],
  (displayAnswersMode, selectedUserId) => {
    switch (displayAnswersMode) {
      case DisplayAnswersMode.AllLatest:
        return 'allLatest'
      case DisplayAnswersMode.AllMyLatest:
        return 'allMyLatest'
      case DisplayAnswersMode.ByUser:
        return selectedUserId
      default:
        return 'allLatest'
    }
  }
)

export const getIsAnswerInProgress = (state: RootState) => state.forms.answerInProgress
export const getIsFormExportInProgress = (state: RootState) => state.forms.formExportInProgress
export const getCurrentFormSelectedRecord = (state: RootState) => getCurrentForm(state)?.selectedRecord
export const getFormAnswerStatusFilter = (state: RootState) => state.forms.answerStatusFilter
export const getSelectedTabs = (state: RootState) => state.forms.currentForm?.selectedTabs || []
export const getFormAnswerStatusStatistics = (state: RootState) => state.forms.answerStatusStatistics
export const getContentReviews = (state: RootState) => state.contentReviews.reviewsList as ContentReview[]
export const getContentOwnerId = (state: RootState) => state.contentState.ownerId
export const getIsExportFormToPdfButtonEnabled = getShowExportFormToPdfButton
export const getCounterValues = (state: RootState) =>
  state.artificialIntelligence.counterValuesList as AIObjectsInfoResponse[]
export const getCurrentFolder = (state: RootState) => state.forms.currentFolder

export const getHasAnswerPermission = (state: RootState) =>
  hasSessionPermission(state, SessionPermission.AnswerQuestions)
export const getHasApprovePermission = (state: RootState) =>
  hasSessionPermission(state, SessionPermission.RejectApprove)
export const getHasAttachFormPermission = (state: RootState) => hasSessionPermission(state, SessionPermission.AddForms)
export const getHasDeleteAllAnswersPermission = (state: RootState) =>
  hasSessionPermission(state, SessionPermission.DeleteAllFormAnswers)
export const getHasExportFormCsvPermission = (state: RootState) =>
  hasSessionPermission(state, SessionPermission.ExportFormAsCsv)
export const getHasExportFormPdfPermission = (state: RootState) =>
  hasSessionPermission(state, SessionPermission.ExportFormAsPdf)
export const getHasCompleteReviewPermission = (state: RootState) =>
  hasSessionPermission(state, SessionPermission.CompleteReview)
export const getHasSendReviewFeedbackPermission = (state: RootState) =>
  hasSessionPermission(state, SessionPermission.SendReviewFeedback)

export const getFormById = createSelector([getContentForms, (_, { Id }) => Id], (contentForms, formId) => {
  return contentForms.find((cf) => cf.FormId === formId)
})

export const getContentFormsForCurrentUser = createSelector(
  [getContentForms, getIsCurrentUserReviewer, getCurrentUserReview],
  (contentForms, isReviewer, currentUserReview?: ContentReview) => {
    if (isReviewer) {
      if (currentUserReview) {
        const filteredForms = contentForms.filter((cf) => currentUserReview.FormsIds!.some((r) => r === cf.FormId))
        return filteredForms
      }
      return contentForms
    }
    return contentForms
  }
)

export const getIsFormHiddenForCurrentUser = createSelector(
  [getCurrentForm, getContentReviews, getIsCurrentUserReviewer, getContentId, getFormTitle],
  (currentForm, reviewsList, isReviewer, contentId, formTitle) => {
    let result = { isHidden: false, textMessage: '' }

    const currentFormReview = reviewsList.find((r) => r.FormsIds!.some((formId) => formId === currentForm.FormId))
    if (!currentFormReview) {
      return result
    }

    const reviewStatus = currentFormReview.ReviewStatus
    if (currentFormReview.Type === ContentSharingType.ForReview) {
      if (isReviewer && reviewStatus !== ContentReviewStatus.Active) {
        result = { isHidden: true, textMessage: `${formTitle} reworking in progress` }
      } else if (
        !isReviewer &&
        (reviewStatus === ContentReviewStatus.Active || reviewStatus === ContentReviewStatus.Pending)
      ) {
        result = { isHidden: true, textMessage: `${formTitle} is exported for review` }
      }
    } else if (currentFormReview.Type === ContentSharingType.MergeForReview) {
      if (contentId === currentFormReview.TargetContentId && reviewStatus !== ContentReviewStatus.Active) {
        result = { isHidden: true, textMessage: `${formTitle} reworking in progress` }
      }

      if (
        contentId !== currentFormReview.TargetContentId &&
        (reviewStatus === ContentReviewStatus.Active || reviewStatus === ContentReviewStatus.Pending)
      ) {
        result = { isHidden: true, textMessage: `${formTitle} is exported for review` }
      }
    }

    return result
  }
)

export const getIsDetachButtonDisabled = createSelector(
  [getContentForms, getContentReviews, getCurrentForm, (_, { form }) => form],
  (contentForms, reviewsList, currentForm, form) => {
    const formItem = form || currentForm
    const contentForm = contentForms.find((cf) => cf.FormId === formItem?.FormId)
    if (!contentForm) return false

    const currentFormReview = reviewsList.find((r) => r.FormsIds!.some((formId) => formId === contentForm.FormId))
    if (!currentFormReview) return false

    const reviewStatus = currentFormReview.ReviewStatus

    if (reviewStatus !== ContentReviewStatus.Completed) {
      return true
    }

    return false
  }
)

export const getIsAddFormButtonVisible = createSelector(
  [getIsCurrentUserPresenter, getHasAttachFormPermission],
  (isPresenter, hasAttachPermission) => {
    return isPresenter || hasAttachPermission
  }
)

export const getIsInitialValuesButtonVisible = createSelector(
  [getDisplayAnswersMode, getSelectedUserId, getCurrentUserId, getCurrentForm, getHasAnswerPermission],
  (displayAnswersMode, selectedUserId, currentUserId, currentForm, hasAnswerPermission) => {
    const answers = currentForm.answers || []
    const controls = currentForm.Controls
    const isSelectedCurrentUser = displayAnswersMode === DisplayAnswersMode.ByUser && selectedUserId === currentUserId

    let hasControlsWithInitialValueWithoutAnswer = false

    if (controls) {
      for (let i = 0; i < controls.length; i++) {
        const control = controls[i]
        const isControlWithInitialValue = control.InitialValue

        const hasAnswer = answers.some((a) => a.FormControlId === control.Id)

        if (isControlWithInitialValue && !hasAnswer) {
          hasControlsWithInitialValueWithoutAnswer = true
          break
        }
      }
    }

    const isVisible = isSelectedCurrentUser && hasControlsWithInitialValueWithoutAnswer && hasAnswerPermission

    return isVisible
  }
)

export const getIsUploadAnswersFromFileButtonVisible = createSelector(
  [getDisplayAnswersMode, getSelectedUserId, getCurrentUserId, getCurrentForm, getHasAnswerPermission],
  (displayAnswersMode, selectedUserId, currentUserId, currentForm, hasAnswerPermission) => {
    const controls = currentForm.Controls || []
    const isSelectedCurrentUser = displayAnswersMode === DisplayAnswersMode.ByUser && selectedUserId === currentUserId

    const isVisible = isSelectedCurrentUser && controls.length > 0 && hasAnswerPermission

    return isVisible
  }
)

export const getHasSendForInContentReviewPermission = (state: RootState) =>
  hasSessionPermission(state, SessionPermission.SendForInContentReview)

export const getHasCompleteOrFeedbackInContentReviewPermission = (state: RootState) =>
  hasSessionPermission(state, SessionPermission.CompleteOrFeedbackInContentReview)

export const getIsSendForInContentReviewButtonVisible = createSelector(
  [getHasSendForInContentReviewPermission, getIsInContentReviewEnabled],
  (hasSendForInContentReviewPermission, isInContentReviewEnabled) => {
    return isInContentReviewEnabled && hasSendForInContentReviewPermission
  }
)

export const getIsSendForInContentReviewButtonDisabled = createSelector(
  [getContentReviewStatus],
  (sessionReviewStatus) => {
    const ReviewActiveStatuses = [ReviewStatus.InReview, ReviewStatus.ReadyForReview]

    return ReviewActiveStatuses.includes(sessionReviewStatus)
  }
)

export const getAreCompleteOrFeedbackInContentReviewButtonsVisible = createSelector(
  [getHasCompleteOrFeedbackInContentReviewPermission, getContentReviewStatus, getIsInContentReviewEnabled],
  (hasCompleteOrFeedbackInContentReviewPermission, sessionReviewStatus, isInContentReviewEnabled) => {
    const isInContentReviewActive =
      sessionReviewStatus === ReviewStatus.InReview || sessionReviewStatus === ReviewStatus.ReadyForReview
    return isInContentReviewEnabled && hasCompleteOrFeedbackInContentReviewPermission && isInContentReviewActive
  }
)

export const getIsFormButtonVisible = createSelector(
  [
    getContentForms,
    getIsAddFormButtonVisible,
    getIsInitialValuesButtonVisible,
    getIsSendForInContentReviewButtonVisible,
    getAreCompleteOrFeedbackInContentReviewButtonsVisible,
    getReviewsList,
    (_, { button }) => button,
  ],
  (
    contentForms,
    isAddFormVisible,
    isInitialValueButtonVisible,
    isSendForInContentReviewButtonVisible,
    areCompleteOrFeedbackInContentReviewButtonsVisible,
    reviews,
    button
  ) => {
    const anyContentForm = contentForms?.length > 0

    switch (button) {
      case FormButtons.SetInitialValues:
        return isInitialValueButtonVisible
      case FormButtons.SendForInContentReview:
        return isSendForInContentReviewButtonVisible && anyContentForm && reviews.length === 0
      case FormButtons.FeedbackInContentReview:
      case FormButtons.CompleteInContentReview:
        return areCompleteOrFeedbackInContentReviewButtonsVisible && anyContentForm
      default:
        return false
    }
  }
)

export const getIsFormButtonDisabled = createSelector(
  [
    getIsSendForInContentReviewButtonDisabled,
    getIsDetachButtonDisabled,
    getCurrentUserReview,
    (_, { button }) => button,
  ],
  (isSendForInContentReviewButtonDisabled, isDetachButtonDisabled, review, button) => {
    switch (button) {
      case FormButtons.SetInitialValues:
        return isDetachButtonDisabled
      case FormButtons.CompleteReview:
      case FormButtons.SendReviewFeedback:
        return review ? review.ReviewStatus !== ContentReviewStatus.Active : true
      case FormButtons.SendForInContentReview:
        return isSendForInContentReviewButtonDisabled
      default:
        return false
    }
  }
)

export const getUploadCsvButtonState = createSelector(
  [getIsDetachButtonDisabled, getIsUploadAnswersFromFileButtonVisible],
  (isDisabled, isVisible) => {
    return { isDisabled, isVisible }
  }
)

export const getFormButtonState = createSelector(
  [getIsFormButtonVisible, getIsFormButtonDisabled, (_, { button }) => button],
  (isVisible, isDisabled) => {
    return { isVisible, isDisabled }
  }
)

export const getFormButtonsGroups = createSelector([getCurrentUserReview], (review) => {
  if (review) {
    const group = review.Type === ContentSharingType.ForReview ? FormButtonsGroup.Review : FormButtonsGroup.MergeReview
    return [group]
  }

  return [FormButtonsGroup.Common]
})

export const getContentFormsInReview = createSelector(
  [getContentForms, getContentReviews, getContentId],
  (contentForms, reviewsList, contentId) => {
    const formsInReview: ContentForm[] = []
    if (reviewsList?.length < 1) {
      return formsInReview
    }

    contentForms.forEach((contentForm) => {
      const reviewWithForm = reviewsList.find((review) =>
        review.FormsIds!.some((formId) => formId === contentForm.FormId)
      )

      if (!reviewWithForm) {
        return
      }

      const isMergeReview = reviewWithForm.Type === ContentSharingType.MergeForReview
      const isMergedForReviewToCurrentContent = isMergeReview && reviewWithForm.TargetContentId === contentId

      if (isMergedForReviewToCurrentContent || reviewWithForm.ReviewStatus !== ContentReviewStatus.FeedbackSent) {
        formsInReview.push(contentForm)
      }
    })

    return formsInReview
  }
)

export const getAvailableContentForms = createSelector(
  [getContentForms, getContentFormsInReview],
  (contentForms, formsInReview) => {
    return contentForms.filter((cf) => !formsInReview.some((f) => cf.Id === f.Id))
  }
)

export const getAnswerStatusColor = (status: FormAnswerStatus) => {
  switch (status) {
    case FormAnswerStatus.Pending:
      return tabeebTheme.palette.info.main
    case FormAnswerStatus.Pass:
    case FormAnswerStatus.ApprovedByAI:
    case FormAnswerStatus.ApprovedByReviewer:
      return tabeebTheme.palette.success.main
    case FormAnswerStatus.Fail:
    case FormAnswerStatus.RejectedByAI:
    case FormAnswerStatus.RejectedByReviewer:
      return tabeebTheme.palette.error.main
    default:
      return tabeebTheme.palette.action.disabled
  }
}

export const getAnswerStatusIcon = (status: FormAnswerStatus) => {
  switch (status) {
    case FormAnswerStatus.Pass:
    case FormAnswerStatus.ApprovedByAI:
    case FormAnswerStatus.ApprovedByReviewer:
      return Approved
    case FormAnswerStatus.Fail:
    case FormAnswerStatus.RejectedByAI:
    case FormAnswerStatus.RejectedByReviewer:
      return Rejected
    default:
      return Submitted
  }
}

export const getAnswerStatus = (
  answer?: FormControlAnswer | null,
  isReviewer?: boolean,
  review?: ContentReview
): FormAnswerStatus => {
  let status: FormAnswerStatus = FormAnswerStatus.Empty

  if (!answer || !answer.Id) {
    status = FormAnswerStatus.Empty
  } else if (isReviewer && review) {
    const editedAnswer = review.EditedFormsAnswers!.find((a) => a.FormAnswerId === answer.Id)

    if (editedAnswer) {
      if (editedAnswer.IsDeleted) {
        if (editedAnswer.ApproverId !== answer.ApproverId) {
          status = FormAnswerStatus.Pending
        } else {
          status = editedAnswer.FormAnswerStatus
          answer.ApproverId = editedAnswer.ApproverId
          answer.ApproverName = editedAnswer.ApproverName
        }
      } else {
        status = editedAnswer.FormAnswerStatus
        answer.ApproverId = editedAnswer.ApproverId
        answer.ApproverName = editedAnswer.ApproverName
      }
    } else {
      status = FormAnswerStatus.Pending
    }
  } else status = answer.FormAnswerStatus

  return status
}

const getSelectedAnswerStatusValue = (
  status: FormAnswerStatus,
  answer: FormControlAnswer | null | undefined,
  currentUserId: number
) => {
  let approverName

  switch (status) {
    case FormAnswerStatus.Pending:
      return 'Submitted'

    case FormAnswerStatus.Pass:
    case FormAnswerStatus.ApprovedByReviewer:
      approverName = answer?.ApproverId === currentUserId ? 'you' : answer?.ApproverName

      return answer?.ApproverName ? `Approved by ${approverName}` : 'Approved'

    case FormAnswerStatus.ApprovedByAI:
      return 'Approved By AI'

    case FormAnswerStatus.Fail:
    case FormAnswerStatus.RejectedByReviewer:
      approverName = answer?.ApproverId === currentUserId ? 'you' : answer?.ApproverName

      return answer?.ApproverName ? `Rejected by ${approverName}` : 'Rejected'

    case FormAnswerStatus.RejectedByAI:
      return 'Rejected By AI'

    default:
      return 'No Action'
  }
}

export const makeGetSelectedAnswerStatus = () =>
  createSelector(
    [
      getCurrentUserId,
      getIsCurrentUserReviewer,
      getCurrentUserReview,
      getHasApprovePermission,
      getCurrentFormSelectedRecord,
      (_, { answer }: { answer?: FormControlAnswer | null }) => answer,
    ],
    (
      currentUserId,
      isReviewer,
      review: ContentReview | undefined,
      hasApprovePermission,
      currentFormSelectedRecord,
      answer
    ) => {
      const answerStatus = getAnswerStatus(answer, isReviewer, review)
      const isFormRecordSelected = Boolean(currentFormSelectedRecord)
      let isDisabled = answerStatus === FormAnswerStatus.Empty || !hasApprovePermission || isFormRecordSelected

      if (answer && ((isReviewer && review?.Type === ContentSharingType.ForReview) || review)) {
        if (review.ReviewStatus !== ContentReviewStatus.Active) {
          isDisabled = true
        }
      }

      const selectedStatus = {
        value: answerStatus,
        displayValue: getSelectedAnswerStatusValue(answerStatus, answer, currentUserId),
        color: getAnswerStatusColor(answerStatus),
        icon: getAnswerStatusIcon(answerStatus),
        isDisabled,
      }

      return selectedStatus
    }
  )

export const getSelectedAnswerStatus = makeGetSelectedAnswerStatus()

export const makeGetAnswerStatusDropdownOptions = () =>
  createSelector([(_, { answer }: { answer?: FormControlAnswer | null }) => answer], (answer) => {
    const answerStatus = getAnswerStatus(answer)

    switch (answerStatus) {
      case FormAnswerStatus.Empty:
        return [
          {
            value: FormAnswerStatus.Empty,
            displayValue: 'No Action',
            color: getAnswerStatusColor(FormAnswerStatus.Empty),
            icon: getAnswerStatusIcon(FormAnswerStatus.Empty),
          },
        ]

      default:
        return [
          {
            value: FormAnswerStatus.Pass,
            displayValue: 'Approve',
            color: getAnswerStatusColor(FormAnswerStatus.Pass),
            icon: getAnswerStatusIcon(FormAnswerStatus.Pass),
          },
          {
            value: FormAnswerStatus.Fail,
            displayValue: 'Reject',
            color: getAnswerStatusColor(FormAnswerStatus.Fail),
            icon: getAnswerStatusIcon(FormAnswerStatus.Fail),
          },
        ]
    }
  })

export const getAnswerStatusDropdownOptions = makeGetAnswerStatusDropdownOptions()

///

export const getIsFormActionDisabled = createSelector(
  [getIsCurrentUserViewer, getCurrentForm, getIsContentReadonly, (_, { actionId }: { actionId: number }) => actionId],
  (isViewer, currentForm, readonlyView) => {
    const isFormRecordSelected = currentForm && !!currentForm.selectedRecord

    return isViewer || isFormRecordSelected || readonlyView
  }
)

export const getIsContentFormOnReview = createSelector(
  [getIsCurrentUserReviewer, getMergeReviewWithCurrentForm],
  (isReviewer, targetContentReview) => {
    if (isReviewer) {
      return true
    }

    return !!targetContentReview
  }
)

export const getIsCurrentFormViewDisabled = createSelector(
  [
    getDisplayAnswersMode,
    getIsAnswerInProgress,
    getSelectedUserId,
    getCurrentUserId,
    getIsCurrentUserViewer,
    getIsCurrentUserReviewer,
    getCurrentForm,
    getHasAnswerPermission,
    getIsContentFormOnReview,
  ],
  (
    displayAnswersMode,
    answerInProgress,
    selectedUserId,
    currentUserId,
    isViewer,
    isReviewer,
    currentForm,
    hasAnswerPermission,
    isFormOnReview
  ) => {
    const isFormRecordSelected = currentForm && !!currentForm.selectedRecord

    return (
      isViewer ||
      isReviewer ||
      (selectedUserId !== currentUserId && displayAnswersMode !== DisplayAnswersMode.AllLatest) ||
      answerInProgress ||
      isFormRecordSelected ||
      !hasAnswerPermission ||
      isFormOnReview
    )
  }
)

export const getIsFormControlOnReviewInOriginalContent = createSelector(
  [getContentReviews, getIsCurrentUserReviewer, getSelectedTabs, getContentId, (_, control: FormControl) => control],
  (reviewsList, isReviewer, selectedTabs, contentId, control) => {
    if (isReviewer) {
      return false
    }

    const review = reviewsList.find((r) =>
      r.FormAnswers!.some((fa) => fa.FormControlId === control.Id && formTabsIdsEquals(fa.FormTabsIds, selectedTabs))
    )

    if (!review || review.TargetContentId === contentId) {
      return false
    }

    return review.ReviewStatus === ContentReviewStatus.Active || review.ReviewStatus === ContentReviewStatus.Pending
  }
)

export const getIsFormUsersFilterVisible = createSelector(
  [getIsCurrentUserReviewer, getIsContentFormOnReview],
  (isReviewer, isOnReview) => {
    if (isReviewer) {
      return false
    }

    return !isOnReview
  }
)

export const getIsDeleteAnswerIconVisible = createSelector(
  [
    getDisplayAnswersMode,
    getHasDeleteAllAnswersPermission,
    getSelectedUserId,
    getCurrentUserId,
    getCurrentForm,
    getIsCurrentUserViewer,
    getIsContentFormOnReview,
    (_, { control }) => control,
  ],
  (
    displayAnswersMode,
    canDeleteAllAnswers,
    selectedUserId,
    currentUserId,
    currentForm,
    isViewer,
    isOnReview,
    control
  ) => {
    const answers = currentForm.answers ? currentForm.answers.filter((item) => item.FormControlId === control.Id) : []
    const isAnswersOwner = currentUserId === selectedUserId && !isViewer
    const isAllLatest = displayAnswersMode === DisplayAnswersMode.AllLatest
    const isFormRecordSelected = currentForm && !!currentForm.selectedRecord

    return (
      (canDeleteAllAnswers || (!isAllLatest && isAnswersOwner)) &&
      answers.length > 0 &&
      !isFormRecordSelected &&
      !isOnReview
    )
  }
)

export const getIsExportFormToPdfOptionVisible = createSelector(
  [getIsExportFormToPdfButtonEnabled, getIsCurrentUserPresenter, getHasExportFormPdfPermission],
  (isExportFormToPdfButtonEnabled, isPresenter, hasExportToPdfPermission) => {
    return isExportFormToPdfButtonEnabled && (isPresenter || hasExportToPdfPermission)
  }
)

export const getCurrentFormControls = createSelector(
  [getIsCurrentUserReviewer, getCurrentUserReview, getCurrentForm, getFormAnswerStatusFilter],
  (isReviewer, review, currentForm, answerStatusFilter) => {
    if (!currentForm || !currentForm.Controls) {
      return []
    }

    let controlsList: FormControl[] = []
    const answers: FormControlAnswer[] = (currentForm.answers || []).map((answer) => ({
      ...answer,
      FormAnswerStatus: getAnswerStatus(answer, isReviewer, review),
    }))

    if (answerStatusFilter.length === Object.keys(AnswerStatusFilterOptions).length) {
      controlsList = currentForm.Controls
    } else {
      for (const control of currentForm.Controls) {
        const controlAnswer = answers.find((answer) => answer.FormControlId === control.Id)

        if (controlAnswer) {
          switch (controlAnswer.FormAnswerStatus) {
            case FormAnswerStatus.Pending:
              if (answerStatusFilter.includes(AnswerStatusFilterOptions.Submitted)) {
                controlsList.push(control)
              }
              break
            case FormAnswerStatus.Pass:
            case FormAnswerStatus.ApprovedByReviewer:
              if (answerStatusFilter.includes(AnswerStatusFilterOptions.Approved)) {
                controlsList.push(control)
              }
              break
            case FormAnswerStatus.Fail:
            case FormAnswerStatus.RejectedByReviewer:
              if (answerStatusFilter.includes(AnswerStatusFilterOptions.Rejected)) {
                controlsList.push(control)
              }
              break
            case FormAnswerStatus.RejectedByAI:
              if (answerStatusFilter.includes(AnswerStatusFilterOptions.RejectedByAI)) {
                controlsList.push(control)
              }
              break
            case FormAnswerStatus.ApprovedByAI:
              if (answerStatusFilter.includes(AnswerStatusFilterOptions.ApprovedByAI)) {
                controlsList.push(control)
              }
              break
            default:
          }
        } else if (answerStatusFilter.includes(AnswerStatusFilterOptions.NoAction)) {
          controlsList.push(control)
        }
      }
    }

    return controlsList
  }
)

export const getIsFormRecordAreaVisible = createSelector(
  [getDisplayAnswersMode, getSelectedUserId, getCurrentUserId, getIsFormRecordsEnabled],
  (displayAnswersMode, selectedUserId, currentUserId, isConfigEnabled) => {
    if (!isConfigEnabled) return false

    const isAllMyLatest = displayAnswersMode === DisplayAnswersMode.AllMyLatest
    const isByUser = displayAnswersMode === DisplayAnswersMode.ByUser

    return isAllMyLatest || (isByUser && selectedUserId !== currentUserId)
  }
)

export const getIsSaveFormRecordButtonVisible = createSelector(
  [getDisplayAnswersMode, getIsCurrentUserViewer, getIsFormRecordsEnabled],
  (displayAnswersMode, isViewer, isFormRecordsEnabled) => {
    const isAllMyLatest = displayAnswersMode === DisplayAnswersMode.AllMyLatest
    return isAllMyLatest && !isViewer && isFormRecordsEnabled
  }
)

export const getFormRecordsList = createSelector(
  [getDisplayAnswersMode, getSelectedUserId, getCurrentUserId, getCurrentForm],
  (displayAnswersMode, selectedUserId, currentUserId, currentForm) => {
    const isAllMyLatest = displayAnswersMode === DisplayAnswersMode.AllMyLatest
    const targetUserId = isAllMyLatest ? currentUserId : selectedUserId
    const records = currentForm.Records || []

    const filteredRecords = records.filter((rec) => rec.UserId === targetUserId)

    return filteredRecords
  }
)

export const getIsDeleteFormRecordButtonVisible = createSelector(
  [getFormRecordsList, getCurrentUserId],
  (records, currentUserId) => {
    const recordsCreator = records.length > 0 ? records[0].UserId : null

    return recordsCreator === currentUserId
  }
)

export const getCurrentFormAnswers = createSelector(
  [getCurrentFormControls, getCurrentForm],
  (controlsList, currentForm) => {
    if (!currentForm || !currentForm.answers) {
      return []
    }

    return controlsList
      .map((control) => {
        const existingAnswer = currentForm.answers!.find((item) => item.FormControlId === control.Id)
        if (existingAnswer) {
          return {
            ...existingAnswer,
            value: existingAnswer.Value,
            selectedOptionsIds: existingAnswer.SelectedOptions?.map((o) => o.Id),
            controlType: control.ControlType,
          }
        }

        return null
      })
      .filter((answer) => !!answer)
  }
)

export const createGetFormControlAnswerSelector = (controlId: number) =>
  createSelector([getCurrentFormAnswers], (answers) => {
    if (!answers) {
      return null
    }

    return answers.find((answer) => answer.FormControlId === controlId)
  })

export const getCurrentPageCounterValues = createSelector(
  [getSelectedGalleryItemId, getCounterValues],
  (currentPageId, counterValuesList) => {
    return counterValuesList.filter((counter) => counter.PageId === currentPageId)
  }
)

export const getCurrentFormCounter = createSelector(
  [getCurrentForm, (_, { counter }) => counter],
  (currentForm, counter) => {
    const { CounterLimit, IncrementValue, IncrementPeriod } = counter
    const { ReattachedNumberOfTimes } = currentForm
    const counterItem = counter

    if (IncrementValue && ReattachedNumberOfTimes) {
      counterItem.calculatedCounterLimit = calculateValue(
        CounterLimit,
        IncrementValue,
        IncrementPeriod,
        ReattachedNumberOfTimes
      )
    } else {
      counterItem.calculatedCounterLimit = CounterLimit
    }

    return counterItem
  }
)

export const getCounterPerSessionStats = createSelector([getCounterValues], (counterValuesList) => {
  const countPerSessionObject: Record<number, { value: number; objects: number[] }> = {}

  counterValuesList.forEach((counter) => {
    const value = counter.ObjectsCount

    if (countPerSessionObject[counter.AIObjectId]) {
      countPerSessionObject[counter.AIObjectId].value += value

      if (
        counter.UniqueObjectId &&
        !countPerSessionObject[counter.AIObjectId].objects.some((v) => v === counter.UniqueObjectId)
      ) {
        countPerSessionObject[counter.AIObjectId].objects.push(counter.UniqueObjectId)
      }
    } else {
      countPerSessionObject[counter.AIObjectId] = {
        value,
        objects: [],
      }
      if (counter.UniqueObjectId) {
        countPerSessionObject[counter.AIObjectId].objects.push(counter.UniqueObjectId)
      }
    }
  })

  return countPerSessionObject
})

export const getFormCounterProps = createSelector(
  [getShowMenuAI, getDisplayAnswersMode, getCurrentPageCounterValues, getCurrentFormCounter, getCounterPerSessionStats],
  (isAiEnabled, displayAnswersMode, currentPageCounterValuesList, counter, countPerSessionObject) => {
    let countPerPage = 0
    let countPerSession = 0

    if (countPerSessionObject[counter.AIObjectId]) {
      if (counter.CountAIObjects) {
        countPerSession = countPerSessionObject[counter.AIObjectId].objects.length

        countPerPage = currentPageCounterValuesList.filter(
          (v) => v.AIObjectId === counter.AIObjectId && v.UniqueObjectId
        ).length
      } else {
        countPerSession = countPerSessionObject[counter.AIObjectId].value

        countPerPage = currentPageCounterValuesList
          .filter((v) => v.AIObjectId === counter.AIObjectId)
          .reduce((sum, curr) => sum + curr.ObjectsCount, 0)
      }
    }

    const { Label, calculatedCounterLimit } = counter
    const showPerPageCount = displayAnswersMode === DisplayAnswersMode.ByUser
    const disabled = !isAiEnabled
    const color = counter.calculatedCounterLimit && countPerSession > counter.calculatedCounterLimit && 'red'
    const text = `${countPerSession} ${calculatedCounterLimit ? ` out of ${calculatedCounterLimit}` : ''}`

    const viewModel = {
      label: Label,
      text,
      countPerPage,
      showPerPageCount,
      disabled,
      color,
    }

    return viewModel
  }
)

export const getIsSignFormButtonVisible = createSelector(
  [getCurrentForm, getIsSaveFormRecordButtonVisible, getCurrentFormSelectedRecord, getIsFormSignatureEnabledInTenant],
  (currentForm, isFormRecordBtnVisible, selectedRecordId, isConfigEnabled) => {
    return currentForm.IsSignatureEnabled && isFormRecordBtnVisible && !selectedRecordId && isConfigEnabled
  }
)

export const getCurrentFormRecordSignature = createSelector([getCurrentForm], (currentForm) => {
  const selectedRecordId = currentForm?.selectedRecord
  if (!currentForm || !selectedRecordId) {
    return null
  }

  const selectedRecord = currentForm.Records?.find((r) => r.Id === selectedRecordId)
  if (!selectedRecord || selectedRecord.Type !== ContentFormRecordType.UserSignature) {
    return null
  }

  return selectedRecord.Signature
})

